const MAX_CANVAS_DIMENSION = 16384
// Prefer a larger source image for readable long-press saves. If the browser
// rejects this canvas size, captureReport retries with the conservative budget.
const MAX_CANVAS_PIXELS = 48000000
const SAFE_CANVAS_PIXELS = 16000000
const MIN_CAPTURE_SCALE = 3
let reportShareUrl = ''
let reportShareBlob = null

const REPORT_IMAGE_ACTION_HINTS = {
  save: '请在打开的图片中长按，选择“保存图片”',
  share: '请在打开的图片中长按，选择“转发给朋友”'
}

function getCaptureScaleForBudget (width, height, devicePixelRatio, maxCanvasPixels) {
  const safeWidth = Math.max(Number(width) || 1, 1)
  const safeHeight = Math.max(Number(height) || 1, 1)
  const requested = Math.max(Number(devicePixelRatio) || 1, MIN_CAPTURE_SCALE)
  return Math.min(
    requested,
    MAX_CANVAS_DIMENSION / safeWidth,
    MAX_CANVAS_DIMENSION / safeHeight,
    Math.sqrt(maxCanvasPixels / (safeWidth * safeHeight))
  )
}

export function getCaptureScale (width, height, devicePixelRatio = 1) {
  return getCaptureScaleForBudget(width, height, devicePixelRatio, MAX_CANVAS_PIXELS)
}

export function getReportShareUrl () {
  return reportShareUrl
}

export function getReportShareBlob () {
  return reportShareBlob
}

export function clearReportShareUrl (urlApi = typeof URL === 'undefined' ? null : URL) {
  if (reportShareUrl.startsWith('blob:') && urlApi && typeof urlApi.revokeObjectURL === 'function') {
    urlApi.revokeObjectURL(reportShareUrl)
  }
  reportShareUrl = ''
  reportShareBlob = null
}

export function setReportShareUrl (url, urlApi = typeof URL === 'undefined' ? null : URL) {
  clearReportShareUrl(urlApi)
  reportShareUrl = String(url || '')
  return reportShareUrl
}

export function createReportImageUploadData (blob, reportId, FormDataApi = FormData) {
  if (!blob || !blob.size) throw new Error('图片生成失败，请重试')
  const safeReportId = String(reportId || 'report').trim() || 'report'
  const formData = new FormDataApi()
  formData.append('file', blob, `sleep-report-${safeReportId}.png`)
  return formData
}

export function normalizeOssImageUrl (value) {
  const imageUrl = String(value || '').trim()
  if (!/^https:\/\//i.test(imageUrl)) {
    throw new Error('截图上传失败，请重试')
  }
  return imageUrl
}

export function getReportImageActionHint (action) {
  return REPORT_IMAGE_ACTION_HINTS[action] || ''
}

function canvasToBlob (canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob || blob.size === 0) reject(new Error('图片生成失败，请重试'))
      else resolve(blob)
    }, 'image/png')
  })
}

export function hasMeaningfulCanvasContent (canvas) {
  const context = canvas && typeof canvas.getContext === 'function' && canvas.getContext('2d')
  if (!context || typeof context.getImageData !== 'function') return false
  const rows = Math.min(canvas.height, 64)
  try {
    for (let index = 0; index < rows; index += 1) {
      const y = Math.min(canvas.height - 1, Math.floor(index * canvas.height / rows))
      const pixels = context.getImageData(0, y, canvas.width, 1).data
      for (let offset = 0; offset < pixels.length; offset += 4) {
        if (pixels[offset + 3] > 0 && (pixels[offset] < 248 || pixels[offset + 1] < 248 || pixels[offset + 2] < 248)) {
          return true
        }
      }
    }
    return false
  } catch (error) {
    // A tainted canvas can still be a valid capture; toBlob remains the final fallback.
    return true
  }
}

export async function captureReport (element, options = {}) {
  if (!element) throw new Error('报告内容尚未加载')
  const width = element.scrollWidth || element.offsetWidth
  const height = element.scrollHeight || element.offsetHeight
  if (!width || !height) throw new Error('报告内容尚未加载')
  const html2canvas = options.html2canvas || (await import('html2canvas')).default
  const pixelRatio = options.devicePixelRatio || (typeof window === 'undefined' ? 1 : window.devicePixelRatio)
  const scales = [
    getCaptureScaleForBudget(width, height, pixelRatio, MAX_CANVAS_PIXELS),
    getCaptureScaleForBudget(width, height, pixelRatio, SAFE_CANVAS_PIXELS)
  ].filter((scale, index, all) => index === all.findIndex(item => item === scale))
  let lastError

  for (const scale of scales) {
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        scale,
        width,
        height,
        windowWidth: width,
        windowHeight: height
      })
      if (!canvas || !canvas.width || !canvas.height) throw new Error('图片生成失败，请重试')
      if (!hasMeaningfulCanvasContent(canvas)) throw new Error('图片内容为空，请重试')
      const blob = await canvasToBlob(canvas)
      const urlApi = options.urlApi || URL
      const shareUrl = setReportShareUrl(urlApi.createObjectURL(blob), urlApi)
      reportShareBlob = blob
      return shareUrl
    } catch (error) {
      lastError = error
    }
  }

  throw lastError || new Error('图片生成失败，请重试')
}
